import React from 'react';
import PropTypes from 'prop-types';
import { group, stringInputSpec } from './editing-configs';
import UniversalContainer, {
  universalContainerPropTypes,
} from '../components/universal-container';

const defaultDimensions = {
  height: '232px',
  width: '100%',
};

export const extractEpisodeId = (url) => {
  const episode = url.match(/.*\/(.*)\/(.*)\?(.*)$/) || [];
  if (episode && episode.length && episode[2]) {
    return episode[2];
  }
  return null;
};

const renderContent = ({
  spotifyEpisodeUrl,
  outputSample,
  podcastMediaHight,
  podcastMediaWidth,
}) => {
  const episode = extractEpisodeId(spotifyEpisodeUrl);
  const src = `https://open.spotify.com/embed/episode/${episode}?utm_source=generator`;

  if (!episode && outputSample) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        width="200px"
        height="auto"
        src="https://www.nerdwallet.com/cdn/img/lpc/spotify-logo.png"
        alt="spotify logo"
        loading="lazy"
      />
    );
  }

  if (!episode) {
    return null;
  }

  return (
    <iframe
      id="spotify-iframe"
      src={src}
      width={podcastMediaWidth || defaultDimensions.width}
      height={podcastMediaHight || defaultDimensions.height}
      frameBorder="0"
      title="Spotify Podcast"
    />
  );
};

const SpotifyPlayer = ({
  spotifyEpisodeUrl,
  podcastMediaWidth = defaultDimensions.width,
  podcastMediaHight = defaultDimensions.height,
  outputSample,
  mediaContainer,
  includeContainer,
}) => {
  if (includeContainer) {
    return (
      <UniversalContainer {...mediaContainer}>
        {renderContent({
          outputSample,
          podcastMediaHight,
          podcastMediaWidth,
          spotifyEpisodeUrl,
        })}
      </UniversalContainer>
    );
  }

  return renderContent({
    outputSample,
    podcastMediaHight,
    podcastMediaWidth,
    spotifyEpisodeUrl,
  });
};

SpotifyPlayer.propTypes = {
  spotifyEpisodeUrl: PropTypes.string,
  podcastMediaWidth: PropTypes.string,
  podcastMediaHight: PropTypes.string,
  outputSample: PropTypes.bool,
  includeContainer: PropTypes.bool,
  mediaContainer: universalContainerPropTypes,
};

SpotifyPlayer.defaultProps = {
  spotifyEpisodeUrl: '',
  podcastMediaWidth: '',
  podcastMediaHight: '',
  outputSample: false,
};

SpotifyPlayer.getEditingConfig = (
  _componentDocument,
  fieldName,
  includeContainer = false
) => {
  const getInputName = (name, topLevel = false) => {
    if (topLevel) {
      return `${fieldName?.split('.').at(-1)}.${name}`;
    }

    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };
  return [
    stringInputSpec(
      getInputName('spotifyEpisodeUrl', true),
      'Podcast episode URL'
    ),
    group('Audio Details', [
      {
        help: 'The desired Spotify podcast episode URL',
      },
      stringInputSpec(getInputName('podcastMediaHight'), 'Podcast height', {
        help: 'The podcast frame height in pixels. Default value is 232px',
      }),
      stringInputSpec(getInputName('podcastMediaWidth'), 'Podcast width', {
        help: 'The podcast frame width in pixels or percentage. Default value is 100%',
      }),
      ...(includeContainer
        ? [
            ...UniversalContainer.getEditingConfig(
              _componentDocument,
              'Container',
              ['padding', 'margin'],
              getInputName('mediaContainer'),
              { disableGroup: true }
            ),
          ]
        : []),
    ]),
  ];
};

export default SpotifyPlayer;
